import React, { FC, useMemo } from "react";

import { useGetArtistTreeQuery } from "@api/graphql";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { Loading } from "@components/loading";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { formatDateStringLong } from "@util/index";

import DetailsRow from "../DetailsRow";

interface CurrentRecipientsProps {
  isArtist?: boolean;
  participantId: number | undefined;
}

const getCurrentRecipientsHeader = () => (
  <>
    <h5>Current Recipients</h5>
  </>
);

export const CurrentRecipients: FC<CurrentRecipientsProps> = ({ isArtist, participantId }) => {
  const { auth } = useAuthenticationContext();
  const { data, loading } = useGetArtistTreeQuery({
    variables: { artistId: participantId ? participantId : 0 },
    skip: !participantId,
  });

  const recipients = data?.getArtistTree?.Nodes;

  const shouldDisplayAccordionContent = useMemo(() => {
    if (isArtist) {
      // Artist No Beneficiaries (internal) => display Artist (participant)
      // Artist with Beneficiary (internal) => display recipients
      // Deceased Artist with immediate beneficiary, 2 recipients (internal) => display recipients
      // So when the participant is an Artist, we display the content
      return true;
    } else {
      // Beneficiary with Artist (internal) => hide content
      // Deceased beneficiary with beneficiaries (internal) => hide content
      return false;
    }
  }, [isArtist]);

  return (
    <ProtectedComponent
      key={"current_recipient"}
      requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices]}
      auth={auth}
    >
      {loading && <Loading />}
      <CustomAccordion
        accordionKey={"currentRecipients"}
        contrastOpenStyle={false}
        header={getCurrentRecipientsHeader()}
        defaultActiveKey={"currentRecipients"}
        customHeaderStyle="px-3"
      >
        {shouldDisplayAccordionContent &&
          recipients &&
          participantId &&
          recipients.map(
            (recipient) =>
              recipient.attributes.active && (
                <div className="pb-4" key={recipient.node_id}>
                  <DetailsRow
                    details={[
                      {
                        title: "Recipient",
                        value: (
                          <ParticipantName
                            name={recipient.attributes.companyName || recipient.attributes.name}
                            participantId={recipient.node_id}
                          />
                        ),
                      },
                      {
                        title: "",
                        value: <ParticipantStatusBadge status={recipient.attributes.participantStatus} />,
                        isBadge: true,
                      },
                      {
                        title: "Share",
                        value: `${recipient.attributes.overall_royalty_percentage}%`,
                      },
                      {
                        title: "Start date",
                        value: formatDateStringLong(recipient.attributes.start_date) || "-",
                      },
                    ]}
                    className="mt-0"
                  />
                </div>
              ),
          )}
      </CustomAccordion>
    </ProtectedComponent>
  );
};
